import * as XLSX from 'xlsx';
import {
  InventoryApiResponse,
  InventorySlotPackage,
  InventorySegment,
} from '../interfaces/InventoryDashboardInterfaces';
import { EXPORT_HEADERS } from '../constants/slotConfigs';

export const EXPORT_HEADERS_COUNT = EXPORT_HEADERS.length - 1;

export function buildSheetRows(data: InventoryApiResponse): (string | number)[][] {
  const rows: (string | number)[][] = [];
  const slotPackages = data.slotPackages || [];

  if (slotPackages.length === 0) {
    rows.push(['No data available']);
    return rows;
  }

  if (slotPackages.length > 1) {
    const superHeader: (string | number)[] = [''];
    slotPackages.forEach((pkg: InventorySlotPackage) => {
      superHeader.push(pkg.slotPackageName);
      for (let i = 1; i < EXPORT_HEADERS_COUNT; i++) {
        superHeader.push('');
      }
    });
    rows.push(superHeader);
  }

  const headerRow: (string | number)[] = [EXPORT_HEADERS[0]];
  slotPackages.forEach(() => {
    headerRow.push(...EXPORT_HEADERS.slice(1));
  });
  rows.push(headerRow);

  const segmentNames = [
    ...new Set(
      slotPackages.flatMap((pkg: InventorySlotPackage) =>
        pkg.segments.map((seg: InventorySegment) => seg.name)
      )
    ),
  ];

  segmentNames.forEach((segmentName) => {
    const row: (string | number)[] = [segmentName];

    slotPackages.forEach((pkg: InventorySlotPackage) => {
      const segment = pkg.segments.find(
        (seg: InventorySegment) => seg.name === segmentName
      );

      if (segment) {
        row.push(
          segment.totalImpression ?? 0,
          segment.overallValue ?? 0,
          (segment.percentageConsumed ?? 0) / 100,
          (segment.percentageAvailable ?? 0) / 100,
          segment.cpm ?? 0,
          segment.typeOfPurchase ?? '-',
          segment.category ?? '-'
        );
      } else {
        row.push('-', '-', '-', '-', '-', '-', '-');
      }
    });

    rows.push(row);
  });

  const totalRow: (string | number)[] = ['Total'];
  slotPackages.forEach((pkg: InventorySlotPackage) => {
    totalRow.push(
      pkg.totalImpressionsCount ?? 0,
      pkg.totalOverallValue ?? 0,
      '-',
      '-',
      '-',
      '-',
      '-'
    );
  });
  rows.push(totalRow);

  return rows;
}

export function applyNumberFormats(
  ws: XLSX.WorkSheet,
  slotPackageCount: number,
  hasSuper: boolean
): void {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  const dataStartRow = hasSuper ? 2 : 1;

  for (let row = dataStartRow; row <= range.e.r; row++) {
    for (let pkg = 0; pkg < slotPackageCount; pkg++) {
      const baseCol = 1 + pkg * EXPORT_HEADERS_COUNT;

      const impressionCell = XLSX.utils.encode_cell({ r: row, c: baseCol });
      if (ws[impressionCell] && typeof ws[impressionCell].v === 'number') {
        ws[impressionCell].z = '#,##0';
      }

      const valueCell = XLSX.utils.encode_cell({ r: row, c: baseCol + 1 });
      if (ws[valueCell] && typeof ws[valueCell].v === 'number') {
        ws[valueCell].z = '#,##0';
      }

      const consumedCell = XLSX.utils.encode_cell({ r: row, c: baseCol + 2 });
      if (ws[consumedCell] && typeof ws[consumedCell].v === 'number') {
        ws[consumedCell].z = '0.00%';
      }

      const availableCell = XLSX.utils.encode_cell({ r: row, c: baseCol + 3 });
      if (ws[availableCell] && typeof ws[availableCell].v === 'number') {
        ws[availableCell].z = '0.00%';
      }

      const cpmCell = XLSX.utils.encode_cell({ r: row, c: baseCol + 4 });
      if (ws[cpmCell] && typeof ws[cpmCell].v === 'number') {
        ws[cpmCell].z = '#,##0.00';
      }
    }
  }
}
